/**
 * QuestionPreview — live, read-only "candidate's-eye" render of the Question
 * currently being authored in `pages/admin/questions/edit.tsx`.
 *
 * This is an AUTHOR preview, not the candidate exam surface:
 *   - Every control is rendered visually but disabled / non-interactive — no
 *     state is wired. It exists so the author can sanity-check shape + copy.
 *   - Correct options are marked subtly (a "✓ correct" mono tag) so the author
 *     can verify answer keys at a glance. This is intentional and admin-only;
 *     it must never ship to a candidate bundle (the real exam render lives in
 *     `pages/take/question-phase.tsx` and omits `is_correct`).
 *
 * Render shapes mirror question-phase.tsx (McqInput / MultiInput / OrderInput /
 * TextInput) but use design.md tokens via Tailwind classes instead of the
 * hardcoded hex inline-styles used there. DO NOT import from question-phase.tsx.
 */

import { type ReactElement } from 'react';
import type { OptionInput, QuestionType } from '../../../../state/questions/api.ts';

export interface QuestionPreviewProps {
  type: QuestionType;
  text: string;
  options: OptionInput[];
  explanation: string;
  imageUrl: string | null;
}

const OPTION_ROW_CLASSES =
  'flex items-center gap-3 rounded-md border border-line-2 bg-card px-3 py-2.5 font-sans text-body text-ink';

/** Small mono "✓ correct" affordance for author-facing answer-key marking. */
function CorrectTag(): ReactElement {
  return (
    <span className="ml-auto shrink-0 rounded font-mono text-eyebrow uppercase tracking-wide text-success">
      ✓ correct
    </span>
  );
}

/** Faux radio/checkbox marker — a visual dot/check, never an input. */
function Marker({
  shape,
  filled,
}: {
  shape: 'radio' | 'checkbox';
  filled: boolean;
}): ReactElement {
  const base =
    shape === 'radio'
      ? 'h-4 w-4 rounded-full border'
      : 'h-4 w-4 rounded-sm border';
  const tone = filled
    ? 'border-accent bg-accent/10'
    : 'border-line-2 bg-card';
  return <span aria-hidden="true" className={`${base} ${tone} shrink-0`} />;
}

function ChoiceList({
  options,
  shape,
}: {
  options: OptionInput[];
  shape: 'radio' | 'checkbox';
}): ReactElement {
  const sorted = [...options].sort((a, b) => a.sort_order - b.sort_order);
  return (
    <ul className="flex flex-col gap-2">
      {sorted.map((opt, idx) => (
        <li key={idx} className={OPTION_ROW_CLASSES}>
          <Marker shape={shape} filled={opt.is_correct === true} />
          <span className={opt.text.length > 0 ? '' : 'text-muted-2 italic'}>
            {opt.text.length > 0 ? opt.text : 'Empty option'}
          </span>
          {opt.is_correct === true ? <CorrectTag /> : null}
        </li>
      ))}
    </ul>
  );
}

function OrderList({ options }: { options: OptionInput[] }): ReactElement {
  const sorted = [...options].sort((a, b) => a.sort_order - b.sort_order);
  return (
    <ol className="flex flex-col gap-2">
      {sorted.map((opt, idx) => (
        <li key={idx} className={OPTION_ROW_CLASSES}>
          <span
            aria-hidden="true"
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent/10 font-mono text-eyebrow text-accent"
          >
            {idx + 1}
          </span>
          <span className={opt.text.length > 0 ? '' : 'text-muted-2 italic'}>
            {opt.text.length > 0 ? opt.text : 'Empty step'}
          </span>
        </li>
      ))}
    </ol>
  );
}

function DisabledTextInput({
  numeric,
}: {
  numeric: boolean;
}): ReactElement {
  return (
    <input
      type={numeric ? 'number' : 'text'}
      disabled
      placeholder={
        numeric ? 'Candidate enters a number' : 'Candidate types their answer'
      }
      aria-hidden="true"
      tabIndex={-1}
      className="w-full max-w-md cursor-not-allowed rounded-md border border-line-2 bg-bg-warm px-3 py-2.5 font-sans text-body text-muted placeholder:text-muted-2"
    />
  );
}

function PreviewBody({
  type,
  options,
}: {
  type: QuestionType;
  options: OptionInput[];
}): ReactElement {
  switch (type) {
    case 'multiple':
    case 'truefalse':
      return <ChoiceList options={options} shape="radio" />;
    case 'multi':
      return <ChoiceList options={options} shape="checkbox" />;
    case 'order':
      return <OrderList options={options} />;
    case 'fillnumber':
      return <DisabledTextInput numeric />;
    case 'short':
    case 'filltext':
      return <DisabledTextInput numeric={false} />;
  }
}

export function QuestionPreview(props: QuestionPreviewProps): ReactElement {
  const { type, text, options, explanation, imageUrl } = props;
  const hasText = text.trim().length > 0;
  const hasExplanation = explanation.trim().length > 0;

  return (
    // aria-hidden + inert-by-design: this is a visual mirror, not a real form.
    // Disabled controls keep it out of the tab order; screen readers get the
    // authoring controls above, not this duplicate.
    <div data-testid="question-preview" className="flex flex-col gap-5">
      {/* Question stem */}
      {hasText ? (
        <p className="font-sans text-body-lg text-ink whitespace-pre-wrap">
          {text}
        </p>
      ) : (
        <p className="font-sans text-body-lg text-muted-2 italic">
          Question text will appear here…
        </p>
      )}

      {/* Optional attached image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="max-h-72 w-auto rounded-md border border-line"
        />
      ) : null}

      {/* Type-specific answer surface */}
      <PreviewBody type={type} options={options} />

      {/* Explanation — clearly separated + labelled as post-submission. */}
      {hasExplanation ? (
        <div className="mt-1 rounded-md border border-line bg-bg-warm px-4 py-3">
          <p className="mb-1 font-mono text-eyebrow uppercase tracking-wide text-muted">
            Explanation (shown after submission)
          </p>
          <p className="font-sans text-small text-ink whitespace-pre-wrap">
            {explanation}
          </p>
        </div>
      ) : null}
    </div>
  );
}
